import type { ThreadSummaryDto } from '../models/orpheus.dto'
import type { ChatThread, WorkspaceId } from '../models/orpheus.types'

import { mapThreadHistory } from './messageMapper'

export function buildThreadState(
  wire: ThreadSummaryDto,
  workspaceId: WorkspaceId,
  existing?: ChatThread,
): ChatThread {
  const ownedExisting =
    existing && String(existing.workspaceId) === String(workspaceId) ? existing : undefined
  const messages = Array.isArray(wire.messages)
    ? mapThreadHistory(
        { messages: wire.messages },
        ownedExisting?.localId ?? wire.local_id ?? wire.thread_id,
      )
    : (ownedExisting?.messages ?? [])
  const lastMessagePreview =
    wire.last_message_preview ??
    wire.last_message?.content ??
    ownedExisting?.lastMessagePreview ??
    ''

  return {
    workspaceId,
    threadId: wire.thread_id,
    localId: ownedExisting?.localId ?? wire.local_id ?? wire.thread_id,
    conversationId: ownedExisting?.conversationId ?? null,
    title: wire.title ?? ownedExisting?.title ?? '',
    bookmarked: wire.bookmarked ?? ownedExisting?.bookmarked ?? false,
    createdAt: wire.created_at ?? ownedExisting?.createdAt ?? null,
    updatedAt: wire.updated_at ?? wire.last_message?.created_at ?? ownedExisting?.updatedAt ?? null,
    lastMessagePreview,
    messageCount: wire.message_count ?? messages.length,
    messages,
    historyLoaded: Array.isArray(wire.messages) || (ownedExisting?.historyLoaded ?? false),
    historyLoading: ownedExisting?.historyLoading ?? false,
  }
}

export function sortThreads(threads: readonly ChatThread[]): ChatThread[] {
  return [...threads].sort((left, right) => {
    if (left.bookmarked !== right.bookmarked) return left.bookmarked ? -1 : 1
    return (
      timestamp(right.updatedAt ?? right.createdAt) - timestamp(left.updatedAt ?? left.createdAt)
    )
  })
}

export function mergeThreads(
  current: readonly ChatThread[],
  incoming: readonly ChatThread[],
): ChatThread[] {
  const merged = new Map<string, ChatThread>()

  for (const thread of current) merged.set(threadKey(thread), thread)
  for (const thread of incoming) {
    const key = threadKey(thread)
    const existing = merged.get(key) ?? findOptimisticMatch(merged, thread)
    if (!existing) {
      merged.set(key, thread)
      continue
    }

    merged.delete(threadKey(existing))
    merged.set(key, {
      ...existing,
      ...thread,
      localId: existing.localId,
      messages: thread.messages.length > 0 ? thread.messages : existing.messages,
      historyLoaded: thread.historyLoaded || existing.historyLoaded,
      historyLoading: thread.historyLoading || existing.historyLoading,
    })
  }

  return sortThreads([...merged.values()])
}

function findOptimisticMatch(
  threads: ReadonlyMap<string, ChatThread>,
  incoming: ChatThread,
): ChatThread | undefined {
  return [...threads.values()].find(
    (candidate) =>
      String(candidate.workspaceId) === String(incoming.workspaceId) &&
      candidate.localId === incoming.localId,
  )
}

function threadKey(thread: ChatThread): string {
  return `${String(thread.workspaceId)}:${thread.threadId ?? `local:${thread.localId}`}`
}

function timestamp(value: string | null): number {
  if (!value) return 0
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}
